<template>
  <div class="header" :class="[`header-logo-${logoAlign}`]">
    <div class="header-inner">
      <div class="header-left">
        <responsive-logo
          v-if="showLogo"
          :src="pageIndexLeftLogoUrl || channel?.logoDetail?.rectangle"
          :horizontal="{
            container: { width: '180px', height: '56px' },
            image: { maxWidth: '180px', maxHeight: '56px' }
          }"
          position="top-left"
          :vertical="{
            container: { height: '80px' },
            image: { maxHeight: '80px' }
          }"
          @click="handleClick"
        />
        <y-breadcrumb :routes="routes" />
      </div>

      <div class="header-right">
        <div class="mr-8" v-if="showCurrency">
          <currency-selector
            v-model="currency"
            @change="handleOnChangeCurrency"
          />
        </div>

        <div class="mr-8" v-if="showLanguage">
          <language-selector
            v-model="language"
            :use-channel-languages="useChannelLanguages"
            @change="handleOnLanguageChange"
          />
        </div>
        <div v-if="channel?.key === 'itis'" class="mr-8">
          <a
            target="_blank"
            href="https://www.itis.com.tw/contact.php"
            class="go-itis"
            >ITIS {{ t("shared.common.Member") }}</a
          >
        </div>
        <current-user
          :show-divider="showLanguage"
          @member="hanldeSetMember"
          :custom-user="customUser"
          :settings="headerSettings"
        />
        <user-hl-member
          :settings="headerSettings"
          v-model:show="memberDrawer"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import CurrentUser from "@packages/user/CurrentUser/index.vue";
import LanguageSelector from "@packages/user/LanguageSelector.vue";
import UserHlMember from "@packages/user/memberUseDrawer/index.vue";
import YBreadcrumb from "@ui/Breadcrumb/index.vue";
import ResponsiveLogo from "@ui/ResponsiveLogo/index.vue";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import CurrencySelector from "@/packages/user/currencySelector/index.vue";
import { useChannelStoreHook } from "@/store/modules/channel";
import { useUserStoreHook } from "@/store/modules/user";

import { headerSettings } from "../../config/index";
import { useYChannelStoreHook } from "../../store/channel";
const props = defineProps({
  routes: {
    type: Array,
    default: () => []
  },
  logoUrl: {
    type: String,
    default: ""
  },
  showLogo: { type: Boolean, default: true },
  showLanguage: { type: Boolean, default: true },
  showCurrency: { type: Boolean, default: true }
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const memberDrawer = ref(false);
const { channel } = useChannelStoreHook();
const channelStore = useYChannelStoreHook();
const userStore = useUserStoreHook();
const { language, currency } = storeToRefs(userStore);
const { configs } = storeToRefs(channelStore);
const useChannelLanguages = computed(() => {
  return configs.value?.useChannelLanguages || false;
});

const pageIndexLeftLogoUrl = computed(() => {
  return props.logoUrl || configs.value?.pages?.Index?.leftLogoUrl || "";
});

const logoAlign = computed(() => {
  return configs.value?.header?.logoAlign || "center";
});

const customUser = computed(() => {
  return configs.value?.customUser;
});

const hanldeSetMember = async () => {
  memberDrawer.value = true;
};

const handleOnLanguageChange = async locale2 => {
  await userStore.changeLanguage(locale2);
  document.title = `${t(route.meta.title)} | ${channel.name}`;
};

const handleOnChangeCurrency = async currency => {
  await userStore.changeCurrency(currency);
};

const handleClick = () => {
  router.push({
    path: "/",
    query: { channel: channel.key }
  });
};

defineOptions({
  name: "PageHeader"
});
</script>

<style lang="scss" scoped>
.header {
  position: absolute;
  width: 100%;
  z-index: 2;
  padding: 32px 64px 0;
  min-width: 1080px;
  height: 200px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 100%
  );

  .header-inner {
    height: 42px;
    margin: 0 auto;
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .header-right {
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;
  }

  .logo-box {
    display: inline-block;
    text-align: left;
  }

  .logo {
    object-fit: contain;
    cursor: pointer;
  }

  :deep(.y-breadcrumb) {
    position: relative;
  }
}

.go-itis {
  color: #fff;
}

.header-logo-left {
  .header-inner {
    align-items: flex-start;
  }

  .logo-box {
    display: block;
    position: static;
    text-align: left;
    margin-bottom: 20px;
    transform: translate(0, 0);
  }
}
</style>
